import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, LoadingState, EmptyState } from '../components/ui';
import type { Client, Subscription } from '../types';
import {
  Building2,
  ArrowLeft,
  CreditCard,
  Mail,
  Phone,
  Globe,
  Calendar,
  Activity,
  CheckCircle2,
  Timer,
  DollarSign,
  Layers,
} from 'lucide-react';

export const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      const response = await api.get<{ data: Client }>(`/admin/clients/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });

  const { data: subscriptions } = useQuery({
    queryKey: ['client-subscriptions', id],
    queryFn: async () => {
      const response = await api.get<{ data: Subscription[] }>(
        `/admin/clients/${id}/subscriptions`
      );
      return response.data.data;
    },
    enabled: !!id,
  });

  const { data: usage } = useQuery({
    queryKey: ['client-usage', id],
    queryFn: async () => {
      const response = await api.get(`/admin/analytics/clients/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  if (clientLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/clients">Clientes</BreadcrumbItem>
          <BreadcrumbItem>A carregar...</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/clients">Clientes</BreadcrumbItem>
          <BreadcrumbItem>Não encontrado</BreadcrumbItem>
        </Breadcrumbs>
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-800 shadow-xs">
          <EmptyState
            title="Cliente não encontrado"
            description="O cliente que está a tentar visualizar não existe ou foi removido."
          />
          <Link
            to="/clients"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-4 font-semibold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para os clientes</span>
          </Link>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'active':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50';
      case 'rejected':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50';
      case 'suspended':
      case 'cancelled':
      case 'expired':
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  };

  const getStatusLabelPT = (status: string) => {
    const labels: Record<string, string> = {
      approved: 'Aprovado',
      pending: 'Pendente',
      rejected: 'Rejeitado',
      suspended: 'Suspenso',
      active: 'Ativo',
      cancelled: 'Cancelado',
      expired: 'Expirado',
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/clients">Clientes</BreadcrumbItem>
        <BreadcrumbItem>{client.company_name}</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link
            to="/clients"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm mb-3 font-semibold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para os clientes</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Building2 className="w-8 h-8 text-primary-500" />
            <span>{client.company_name}</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Perfil cadastral, métricas de consumo e subscrições ativas
          </p>
        </div>
        <div>
          <span
            className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-bold border capitalize shadow-2xs ${getStatusBadge(
              client.status
            )}`}
          >
            {getStatusLabelPT(client.status)}
          </span>
        </div>
      </div>

      {/* Client Information Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <span>Informações Cadastrais</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <Building2 className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                Nome da Empresa
              </p>
              <p className="font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {client.company_name}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <CreditCard className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                NIF (Tax ID)
              </p>
              <p className="font-mono font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {client.tax_id}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <Mail className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                Email
              </p>
              <p className="font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {client.email}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <Phone className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                Telefone
              </p>
              <p className="font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {client.phone || 'Não informado'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <Globe className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                País
              </p>
              <p className="font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {client.country}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0">
              <Calendar className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                Registado em
              </p>
              <p className="font-semibold text-slate-900 dark:text-white mt-1 text-sm">
                {new Date(client.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Statistics */}
      {usage && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
            Estatísticas de Uso (Últimos 30 Dias)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Total de Pedidos
                </span>
                <Activity className="w-4 h-4 text-primary-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {usage.total_requests?.toLocaleString() || 0}
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Taxa de Sucesso
                </span>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {usage.total_requests > 0
                  ? `${((usage.successful_requests / usage.total_requests) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Tempo Médio
                </span>
                <Timer className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {usage.average_response_time
                  ? `${usage.average_response_time.toFixed(0)}ms`
                  : '0ms'}
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Gasto Total
                </span>
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                ${usage.total_revenue?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Active Subscriptions */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary-500" />
          <span>Subscrições Ativas</span>
        </h2>

        {subscriptions && subscriptions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {subscriptions.map((subscription) => {
              const quotaUsed = subscription.usage?.current_usage || 0;
              const quotaMax = subscription.plan?.monthly_quota || 1;
              const quotaPercent = Math.min(Math.round((quotaUsed / quotaMax) * 100), 100);

              return (
                <div
                  key={subscription.id}
                  className="rounded-2xl p-5 bg-slate-50/60 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/80 hover:shadow-md transition-all group"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <h3 className="font-bold text-base text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {subscription.product?.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-700 text-slate-700 dark:text-slate-300 capitalize">
                          Plano: {subscription.plan?.tier}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          Início: {new Date(subscription.start_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-bold border capitalize ${getStatusBadge(
                        subscription.status
                      )}`}
                    >
                      {getStatusLabelPT(subscription.status)}
                    </span>
                  </div>

                  {subscription.usage && (
                    <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-slate-700/60">
                      <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-medium text-slate-500 dark:text-slate-400">
                          Consumo Mensal
                        </span>
                        <span className="font-bold font-mono text-slate-800 dark:text-slate-200">
                          {quotaUsed.toLocaleString()} / {quotaMax.toLocaleString()} ({quotaPercent}
                          %)
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            quotaPercent > 90
                              ? 'bg-gradient-to-r from-rose-500 to-rose-600'
                              : quotaPercent > 70
                                ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                                : 'bg-gradient-to-r from-primary-600 to-indigo-500'
                          }`}
                          style={{
                            width: `${quotaPercent}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <EmptyState
              title="Nenhuma subscrição ativa"
              description="Este cliente não possui nenhuma subscrição ativa neste momento."
            />
          </div>
        )}
      </div>
    </div>
  );
};
